import * as turf from "@turf/turf";
import { LngLatLike } from "mapbox-gl";
import { GlobalStoreContextType } from "../context/GlobalStoreContext";
import { Area } from "./Area";
import { Dropoff } from "./Dropoff";
import { Equipment } from "./Equipment";
import { Gate } from "./Gate";
import { GateToDropoffRoute } from "./GateToDropoffRoute";
import { HaulRoute } from "./HaulRoute";
import { geometry, MapObjectModel } from "./MapObject.model";

// Equipment/letter/number icon names embed their displayed color as a suffix (eg.
// "tower_crane_orange") - keep MapObject#color in sync with that suffix so anything reading
// .color directly (legends, dashboards, PDF exports) matches what the icon itself shows.
// Mirrors db/migrate/20260915010000_backfill_map_object_color_from_icon.rb - keep hexes in sync.
const ICON_COLOR_HEXES: Record<string, string> = {
    black: "#000000",
    blue: "#3B82F6",
    green: "#22C55E",
    orange: "#FF9E00",
    pink: "#EC4899",
    purple: "#A855F7",
    red: "#EF4444",
    turquoise: "#00C2C2",
    yellow: "#FACC15",
};

export class MapObject extends MapObjectModel {
    static colorForIcon(icon: string | null): string | null {
        if (!icon) {
            return null;
        }

        const match = Object.entries(ICON_COLOR_HEXES).find(([colorWord]) => icon.includes(colorWord));

        return match ? match[1] : null;
    }

    // TODO: We are still using the non-generated model version of equipment
    getMappable(
        globalStoreContext: GlobalStoreContextType
    ): Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute | null {
        if (this.mappableType === "Area") {
            const projectAreas = globalStoreContext.mapAreasForProject(this.projectId);
            const area = projectAreas.find((area) => area.id === this.mappableId);

            if (area) {
                return area;
            }
        } else if (this.mappableType === "Dropoff") {
            const projectDropoffs = globalStoreContext.dropoffsForProject(this.projectId);
            const dropoff = projectDropoffs.find((dropoff) => dropoff.id === this.mappableId);

            if (dropoff) {
                return dropoff;
            }
        } else if (this.mappableType === "Equipment") {
            const projectEquipments = globalStoreContext.equipmentForProject(this.projectId);
            const equipment = projectEquipments.find((equipment) => equipment.id === this.mappableId);

            if (equipment) {
                return equipment;
            }
        } else if (this.mappableType === "Gate") {
            const projectGates = globalStoreContext.gatesForProject(this.projectId);
            const gate = projectGates.find((gate) => gate.id === this.mappableId);

            if (gate) {
                return gate;
            }
        } else if (this.mappableType === "HaulRoute") {
            const projectHaulRoutes = globalStoreContext.haulRoutesForProject(this.projectId);
            const haulRoute = projectHaulRoutes.find((haulRoute) => haulRoute.id === this.mappableId);

            if (haulRoute) {
                return haulRoute;
            }
        } else if (this.mappableType === "GateToDropoffRoute") {
            const projectGateToDropoffRoutes = globalStoreContext.gateToDropoffRoutesForProject(this.projectId);
            const gateToDropoffRoute = projectGateToDropoffRoutes.find((route) => route.id === this.mappableId);

            if (gateToDropoffRoute) {
                return gateToDropoffRoute;
            }
        }

        return null;
    }

    mapBoxGeometry(): GeoJSON.Geometry["type"] {
        if (this.geometry === "point") {
            return "Point" as GeoJSON.Geometry["type"];
        }
        if (this.geometry === "lineString") {
            return "LineString" as GeoJSON.Geometry["type"];
        }
        if (this.geometry === "polygon") {
            return "Polygon" as GeoJSON.Geometry["type"];
            // } else if (this.geometry.type === "MultiPoint") {
            //     return "MultiPoint";
            // } else if (this.geometry.type === "MultiLineString") {
            //     return "MultiLineString";
            // } else if (this.geometry.type === "MultiPolygon") {
            //     return "MultiPolygon";
        } else {
            throw new Error("Unsupported geometry type");
        }
    }
    mapboxIconAnchor(): mapboxgl.Anchor {
        switch (this.iconAnchor) {
            case "center":
                return "center";
            case "left":
                return "left";
            case "right":
                return "right";
            case "top":
                return "top";
            case "bottom":
                return "bottom";
            case "top_left":
                return "top-left";
            case "top_right":
                return "top-right";
            case "bottom_left":
                return "bottom-left";
            case "bottom_right":
                return "bottom-right";
            default:
                throw new Error("Unsupported icon anchor type");
        }
    }

    static fromFeature(
        feature: GeoJSON.Feature,
        project,
        mappableType: "dropoff" | "location" | "equipment" | "gate" | "haul_route" | "gate_to_dropoff_route"
    ): MapObject {
        const defaultIcon = (geometry: string | geometry): string | null => {
            switch (geometry.toLowerCase()) {
                case "point":
                    return "bullet_sdf_icon";
                case "polygon":
                    return null; // No default icon for polygons, they will use the fill color
                case "linestring":
                    return "arrow_sdf_icon";
                default:
                    return "bullet_sdf_icon";
            }
        };

        const icon = (): string | null => {
            switch (mappableType) {
                case "equipment":
                    return "tower_crane_yellow";
                case "gate":
                    return "square-1-solid-full";
                // Routes are lines - no default directional arrow icon repeated along them.
                case "haul_route":
                case "gate_to_dropoff_route":
                    return null;
                case "dropoff":
                    if (feature.geometry.type === "Point") {
                        return "dropoff_blue";
                    }
                    return defaultIcon(feature.geometry.type);
                case "location":
                    return defaultIcon(feature.geometry.type);
                default:
                    return defaultIcon(feature.geometry.type);
            }
        };

        const randomColor = (): string => "#" + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, "0");

        const color = (): string => {
            switch (mappableType) {
                case "gate":
                    return ICON_COLOR_HEXES.orange;
                case "haul_route":
                    return ICON_COLOR_HEXES.blue;
                case "equipment":
                    return ICON_COLOR_HEXES.yellow;
                case "dropoff":
                    // A point's color follows its dropoff_blue icon; zones have no icon and stay purple.
                    return feature.geometry.type === "Point" ? ICON_COLOR_HEXES.blue : ICON_COLOR_HEXES.purple;
                case "location":
                    return ICON_COLOR_HEXES.green;
                default:
                    return randomColor();
            }
        };

        const mappableTypeName = (): string => {
            switch (mappableType) {
                case "dropoff":
                    return "Dropoff";
                case "location":
                    return "Area";
                case "equipment":
                    return "Equipment";
                case "haul_route":
                    return "HaulRoute";
                case "gate_to_dropoff_route":
                    return "GateToDropoffRoute";
                default:
                    return "Gate";
            }
        };

        return MapObject.fromApiObject(
            {
                color: color(),
                outline: feature.geometry.type === "Polygon" ? "#000000" : null,
                opacity: feature.geometry.type === "Polygon" ? 0.6 : 1,
                geometry: (feature.geometry.type.charAt(0).toLowerCase() + feature.geometry.type.slice(1)) as geometry,
                coordinates: feature.geometry.coordinates,
                icon: icon(),
                icon_anchor: "center",
                icon_units: null,
                icon_width: null,
                mappable_id: -1,
                mappable_type: mappableTypeName(),
                project_id: project.id,
                uuid: feature.id,
                archived: false,
            },
            project.timezone
        );
    }

    getCenter(): LngLatLike {
        switch (this.geometry) {
            case "lineString": {
                const midpointIndex = Math.round((this.coordinates.length - 1) / 2);
                const midpoint = this.coordinates[midpointIndex];
                return midpoint;
            }
            case "polygon": {
                const polygon = turf.polygon(this.coordinates);
                const centroid = turf.centroid(polygon);
                return centroid.geometry.coordinates as LngLatLike;
            }
            default:
                return this.coordinates;
        }
    }

    toFeature(): GeoJSON.Feature {
        return {
            id: this.uuid,
            type: "Feature",
            properties: {
                uuid: this.uuid,
                id: this.uuid,
                icon: this.icon,
                iconAnchor: this.iconAnchor,
                iconWidth: this.iconWidth,
                iconUnits: this.iconUnits,
                color: this.color,
                opacity: this.opacity,
                mappableId: this.mappableId,
                mappableType: this.mappableType,
                tolerance: 0,
            },
            geometry: {
                type: this.mapBoxGeometry(),
                coordinates: this.coordinates,
            },
        };
    }

    setVisibility(map: mapboxgl.Map, visibility: "visible" | "none") {
        switch (this.geometry) {
            case "point":
                if (map.getLayer(this.uuid)) {
                    map.setLayoutProperty(this.uuid, "visibility", visibility);
                }
                break;
            case "lineString":
                if (map.getLayer(this.uuid)) {
                    map.setLayoutProperty(this.uuid, "visibility", visibility);
                }
                if (map.getLayer(`${this.uuid}_line_icon`)) {
                    map.setLayoutProperty(`${this.uuid}_line_icon`, "visibility", visibility);
                }
                if (map.getLayer(`${this.uuid}_lineCaps`)) {
                    map.setLayoutProperty(`${this.uuid}_lineCaps`, "visibility", visibility);
                }
                if (map.getLayer(`${this.uuid}_direction_arrow`)) {
                    map.setLayoutProperty(`${this.uuid}_direction_arrow`, "visibility", visibility);
                }
                break;
            case "polygon":
                if (map.getLayer(this.uuid)) {
                    map.setLayoutProperty(this.uuid, "visibility", visibility);
                }
                if (map.getLayer(`${this.uuid}_outline`)) {
                    map.setLayoutProperty(`${this.uuid}_outline`, "visibility", visibility);
                }
                if (map.getLayer(`${this.uuid}_outline_icon`)) {
                    map.setLayoutProperty(`${this.uuid}_outline_icon`, "visibility", visibility);
                }
                break;
            default:
                throw new Error("Unsupported geometry type for toggling visibility");
        }

        if (this.radiuses && this.radiuses?.length > 0) {
            this.radiuses.forEach((radius) => {
                radius.toggleVisibility(map, this, visibility);
            });
        }
    }

    removeLayerFromMapbox(map: mapboxgl.Map) {
        if (map.getLayer(this.uuid)) {
            map.removeLayer(this.uuid);
        }
        if (this.geometry == "polygon") {
            if (map.getLayer(`${this.uuid}_outline`)) {
                map.removeLayer(`${this.uuid}_outline`);
            }
            if (map.getLayer(`${this.uuid}_outline_icon`)) {
                map.removeLayer(`${this.uuid}_outline_icon`);
            }
        }
        if (this.geometry == "lineString") {
            if (map.getLayer(`${this.uuid}_line_icon`)) {
                map.removeLayer(`${this.uuid}_line_icon`);
            }
            if (map.getLayer(`${this.uuid}_lineCaps`)) {
                map.removeLayer(`${this.uuid}_lineCaps`);
            }
            if (map.getLayer(`${this.uuid}_direction_arrow`)) {
                map.removeLayer(`${this.uuid}_direction_arrow`);
            }
        }
        if (this.radiuses && this.radiuses?.length > 0) {
            this.radiuses.forEach((radius) => {
                radius.removeLayerFromMapbox(map, this);
            });
        }
    }
}
